
import { useState, useRef, useEffect } from 'react';
import { MockGooglePlace } from '@/utils/mockGoogleMapsApi';
import { useGoogleBusinessSearch } from '@/hooks/useGoogleBusinessSearch';
// import { useDataContext } from '@/context/DataContext'; // Removed

export const useBusinessSearch = (initialValue = '') => {
  const { searchBusinesses, isLoading, error } = useGoogleBusinessSearch();
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [searchResults, setSearchResults] = useState<MockGooglePlace[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [usingMockData, setUsingMockData] = useState(false);
  // const { appData } = useDataContext(); // Removed
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchBusinessesRef = useRef(searchBusinesses);
  
  // Check for API key primarily from environment variables, then localStorage as fallback
  const hasApiKey = !!import.meta.env.VITE_GOOGLE_MAPS_API_KEY || !!localStorage.getItem('google_maps_api_key');

  // Store searchBusinesses in ref to avoid dependency changes causing re-runs
  useEffect(() => {
    searchBusinessesRef.current = searchBusinesses;
  }, [searchBusinesses]);

  // Update search term
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  // Set initial value if provided - only run once
  useEffect(() => {
    if (initialValue && initialValue !== searchTerm) {
      setSearchTerm(initialValue);
    }
  }, [initialValue]); // Remove searchTerm from dependencies to prevent loops

  // Search for businesses when user types
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    
    if (searchTerm.length < 2) {
      setSearchResults([]);
      setUsingMockData(false);
      setIsDropdownOpen(false);
      return;
    }
    
    // Set new timeout for debounce
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      
      try {
        const results = await searchBusinessesRef.current(searchTerm);
        
        // Check if we're using mock data (if the place_id starts with 'google-')
        const isMockData = results.length > 0 && results[0].place_id.startsWith('google-');
        setUsingMockData(isMockData);
        
        setSearchResults(results);
        setIsDropdownOpen(results.length > 0 || searchTerm.length >= 2);
      } catch (err) {
        console.error("Error fetching businesses:", err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    
    // Cleanup function will clear the timeout if the component unmounts
    // or if the searchTerm changes before the timeout executes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    };
  }, [searchTerm]); // Remove searchBusinesses from dependencies - use ref instead

  // Toggle dropdown
  const toggleDropdown = (isOpen?: boolean) => {
    setIsDropdownOpen(isOpen !== undefined ? isOpen : !isDropdownOpen);
  };

  return {
    searchTerm,
    searchResults,
    isDropdownOpen,
    isSearching,
    isLoading,
    usingMockData,
    error,
    hasApiKey,
    handleSearchChange,
    toggleDropdown,
    setIsDropdownOpen
  };
};

export default useBusinessSearch;
